import { useState } from 'react';
import { format, parseISO } from 'date-fns';
import { Trash2 } from 'lucide-react';

import { useAdmins, useGrantAdmin, useRevokeAdmin, useSetAdminRole } from '@/lib/api/adminHooks';
import { useBootstrap } from '@/lib/api/hooks';
import type { AdminRole } from '@/types/admin';

export function AdminsScreen() {
  const { data: bootstrap } = useBootstrap();
  const { data, isLoading, isError } = useAdmins();
  const grant = useGrantAdmin();
  const revoke = useRevokeAdmin();
  const setRole = useSetAdminRole();

  const [email, setEmail] = useState('');
  const [grantRole, setGrantRole] = useState<AdminRole>('support');
  const [lastAction, setLastAction] = useState<string | null>(null);

  // Support-role admins see this tab read-only; every mutation below is
  // also enforced server-side (require_full_admin).
  const canManage = bootstrap?.admin_role === 'admin';

  return (
    <div className="flex flex-col gap-24">
      <div className="flex flex-col gap-8">
        <h1 className="type-heading-xl">Admins</h1>
        <p className="type-body-md text-ink-muted max-w-content-sm">
          Grant access by email. The target must already have a patient row
          in Supabase (i.e., they signed up through the portal at least
          once). Four roles: <b>Admin</b> (full — this portal and the
          clinician portal), <b>Support</b> (this portal, except changing
          roles here and writing feature flags), and <b>Clinical</b> (the
          clinician portal <i>only</i>), and <b>Clinical Supervisor</b>
          (clinician portal plus team routing settings). Setting either
          clinical role takes away admin access. You can&rsquo;t revoke
          or re-role yourself — ask another admin.
        </p>
      </div>

      {canManage && (
      <div className="p-16 border border-line rounded-lg bg-raised">
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            setLastAction(null);
            const trimmed = email.trim();
            if (!trimmed) return;
            try {
              const result = await grant.mutateAsync({ email: trimmed, role: grantRole });
              if (result.granted) {
                setLastAction(
                  result.reason === 'already_admin'
                    ? `${trimmed} is already an admin.`
                    : `Granted admin to ${trimmed}.`
                );
                setEmail('');
              } else {
                setLastAction(
                  result.reason === 'patient_not_found'
                    ? `No patient with email ${trimmed} — they need to sign up first.`
                    : `Grant failed: ${result.reason || 'unknown'}`
                );
              }
            } catch (err) {
              setLastAction(`Grant failed: ${String(err)}`);
            }
          }}
          className="flex flex-wrap items-center gap-8"
        >
          <input
            type="email"
            required
            placeholder="email@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="flex-1 min-w-[260px] bg-sunken border border-line rounded px-10 py-6 type-label-sm outline-none focus:outline-none"
          />
          <select
            value={grantRole}
            onChange={(e) => setGrantRole(e.target.value as AdminRole)}
            aria-label="Role for new admin"
            className="bg-sunken border border-line rounded px-10 py-6 type-label-sm"
          >
            <option value="support">Support</option>
            <option value="admin">Admin</option>
            <option value="clinical">Clinical (portal only)</option>
            <option value="clinical_supervisor">Clinical supervisor</option>
          </select>
          <button
            type="submit"
            disabled={grant.isPending || !email.trim()}
            className="px-14 py-6 type-label-sm bg-ink text-ink-onAccent rounded hover:opacity-90 disabled:opacity-50"
          >
            {grant.isPending ? 'Granting…' : 'Grant admin'}
          </button>
        </form>
        {lastAction && (
          <p className="type-label-sm text-ink-muted mt-8">{lastAction}</p>
        )}
      </div>
      )}
      {!canManage && (
        <p className="type-label-sm text-ink-muted border border-line rounded-lg bg-raised p-12">
          You have the <b>Support</b> role — this tab is read-only. A full
          Admin can grant access or change roles.
        </p>
      )}

      <div className="border border-line rounded-lg bg-raised overflow-hidden">
        <div className="p-16 border-b border-line">
          <h2 className="type-heading-md">Current admins</h2>
        </div>

        {isLoading && (
          <div className="p-16 text-center text-ink-muted type-label-md">
            Loading…
          </div>
        )}
        {isError && (
          <div className="p-16 text-center text-ink-muted">Failed to load.</div>
        )}

        {data && (
          <div className="overflow-x-auto">
            <table className="w-full type-label-sm">
              <thead className="bg-sunken border-b border-line text-left text-ink-muted">
                <tr>
                  <Th>Email</Th>
                  <Th>Role</Th>
                  <Th>Display name</Th>
                  <Th>Updated</Th>
                  <Th align="right">Actions</Th>
                </tr>
              </thead>
              <tbody>
                {data.admins.map((a) => {
                  const isSelf = bootstrap?.patient.email?.toLowerCase() === (a.email || '').toLowerCase();
                  return (
                    <tr key={a.id} className="border-t border-line-subtle">
                      <Td>
                        {a.email || <span className="text-ink-muted">—</span>}
                        {isSelf && (
                          <span className="ml-8 text-11 uppercase tracking-wide text-ink-muted">
                            (you)
                          </span>
                        )}
                      </Td>
                      <Td>
                        {canManage && !isSelf ? (
                          <select
                            value={a.role}
                            disabled={setRole.isPending}
                            aria-label={`Role for ${a.email || a.id}`}
                            onChange={async (e) => {
                              const role = e.target.value as AdminRole;
                              try {
                                await setRole.mutateAsync({ patientId: a.id, role });
                                setLastAction(`Set ${a.email || a.id} to ${role}.`);
                              } catch (err) {
                                setLastAction(`Role change failed: ${String(err)}`);
                              }
                            }}
                            className="bg-sunken border border-line rounded px-8 py-4 type-label-sm"
                          >
                            <option value="admin">Admin</option>
                            <option value="support">Support</option>
                            <option value="clinical">Clinical (portal only)</option>
                            <option value="clinical_supervisor">Clinical supervisor</option>
                          </select>
                        ) : (
                          <span className="uppercase tracking-wide text-11 border border-line px-6 py-2 rounded">
                            {a.role}
                          </span>
                        )}
                      </Td>
                      <Td>{a.display_name || '—'}</Td>
                      <Td>
                        {a.updated_at
                          ? format(parseISO(a.updated_at), 'MMM d, yyyy')
                          : '—'}
                      </Td>
                      <Td align="right">
                        {canManage && (
                        <button
                          type="button"
                          disabled={isSelf || revoke.isPending}
                          onClick={async () => {
                            if (!confirm(`Revoke admin from ${a.email || a.id}?`)) return;
                            try {
                              await revoke.mutateAsync(a.id);
                              setLastAction(`Revoked admin from ${a.email || a.id}.`);
                            } catch (err) {
                              setLastAction(`Revoke failed: ${String(err)}`);
                            }
                          }}
                          className="inline-flex items-center gap-4 px-8 py-4 rounded border border-line hover:bg-sunken disabled:opacity-40"
                          title={isSelf ? 'Cannot revoke yourself' : 'Revoke admin'}
                        >
                          <Trash2 size={12} strokeWidth={1.5} />
                          Revoke
                        </button>
                        )}
                      </Td>
                    </tr>
                  );
                })}
                {data.admins.length === 0 && (
                  <tr>
                    <td colSpan={5} className="p-16 text-center text-ink-muted">
                      No admins found. (That's weird — you wouldn't see this
                      screen if there were zero. Try refreshing.)
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function Th({ children, align }: { children: React.ReactNode; align?: 'right' }) {
  return (
    <th
      className={`px-12 py-8 font-medium ${
        align === 'right' ? 'text-right' : ''
      }`}
    >
      {children}
    </th>
  );
}

function Td({ children, align }: { children: React.ReactNode; align?: 'right' }) {
  return (
    <td className={`px-12 py-8 ${align === 'right' ? 'text-right' : ''}`}>{children}</td>
  );
}
